import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Loader2 } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { uploadMediaChunked, fileContentType, ACCEPT_MEDIA } from "@/lib/upload";
import { formatApiError } from "@/lib/api";

// Chunked multi-file uploader with per-file progress. Calls onUploaded(item)
// for each completed file: { id, url, type, filename }.
export default function MediaUploader({ onUploaded, accept = ACCEPT_MEDIA, multiple = true, label = "Upload photos or videos", hint = "JPG, PNG, WEBP, HEIC up to 15MB · MP4, MOV, WEBM up to 200MB", testId = "media-uploader" }) {
  const inputRef = useRef(null);
  const [jobs, setJobs] = useState([]);
  const busy = jobs.some((j) => j.status === "uploading");

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const valid = files.filter((f) => {
      if (!fileContentType(f)) {
        toast.error(`${f.name}: unsupported file type`);
        return false;
      }
      return true;
    });
    for (const file of valid) {
      const key = `${file.name}-${file.size}-${Date.now()}`;
      setJobs((j) => [...j, { key, name: file.name, progress: 0, status: "uploading" }]);
      try {
        const result = await uploadMediaChunked(file, (p) => setJobs((j) => j.map((x) => (x.key === key ? { ...x, progress: p } : x))));
        setJobs((j) => j.filter((x) => x.key !== key));
        onUploaded?.(result);
        toast.success(`${file.name} uploaded`);
      } catch (err) {
        setJobs((j) => j.map((x) => (x.key === key ? { ...x, status: "error", error: formatApiError(err, "Upload failed") } : x)));
        toast.error(`${file.name}: ${formatApiError(err, "Upload failed")}`);
      }
    }
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div data-testid={testId}>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
        disabled={busy}
        className="flex w-full flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-[#2e2e39] bg-[#0f0f15] px-4 py-7 text-center transition-colors hover:border-amber-400/60 disabled:opacity-60"
        data-testid={`${testId}-dropzone`}
      >
        {busy ? <Loader2 className="h-6 w-6 animate-spin text-amber-400" /> : <Upload className="h-6 w-6 text-amber-400" />}
        <span className="text-sm font-semibold text-white">{label}</span>
        <span className="text-xs text-neutral-500">{hint}</span>
      </button>
      <input ref={inputRef} type="file" accept={accept} multiple={multiple} className="hidden" onChange={(e) => handleFiles(e.target.files)} data-testid={`${testId}-input`} />
      {jobs.length > 0 && (
        <ul className="mt-3 space-y-2">
          {jobs.map((j) => (
            <li key={j.key} className="rounded-lg border border-[#20202a] bg-[#101017] p-3 text-xs">
              <div className="flex items-center justify-between gap-3">
                <span className="truncate text-neutral-200">{j.name}</span>
                <span className={j.status === "error" ? "text-red-300" : "text-amber-300"}>{j.status === "error" ? "Failed" : `${j.progress}%`}</span>
              </div>
              {j.status === "uploading" && <Progress value={j.progress} className="mt-2 h-1.5 bg-[#1a1a22]" />}
              {j.error && <p className="mt-1 text-red-300">{j.error}</p>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
